import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Save,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { NumberInput } from './ui/number-input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { isEuCountry, vatPrefix } from '../lib/countries';
import { apiService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';

// Les 4 situations du droit belge. Seul l'assujetti facture ses inscriptions
// avec TVA — les autres régimes produisent des documents sans TVA, avec une
// mention légale distincte.
const VAT_STATUSES = ['subject', 'exempt_art44', 'franchise', 'not_subject'] as const;
type VatStatus = (typeof VAT_STATUSES)[number];

const STATUSES_WITHOUT_VAT: VatStatus[] = ['exempt_art44', 'franchise', 'not_subject'];

// Le franchisé reste un assujetti : il ne facture pas la TVA mais possède bien
// un numéro. L'exonéré art. 44 et le non-assujetti peuvent ne pas en avoir.
const STATUSES_REQUIRING_NUMBER: VatStatus[] = ['subject', 'franchise'];

interface VatRegime {
  vat_status: VatStatus | null;
  vat_number: string | null;
  registration_vat_rate: number | null;
  country: string | null;
  vat_validated_at: string | null;
  vat_validated_name: string | null;
  vat_validated_address: string | null;
  vat_declared_at: string | null;
  is_declared: boolean;
  legal_name: string | null;
  company_number: string | null;
  billing_street: string | null;
  billing_postal_code: string | null;
  billing_city: string | null;
  identity_complete: boolean;
  warning?: string | null;
}

// 0801403607 → 0801.403.607, le format de la BCE.
const formatCompanyNumber = (n: string | null) =>
  n && /^\d{10}$/.test(n) ? `${n.slice(0, 4)}.${n.slice(4, 7)}.${n.slice(7)}` : n ?? '';

interface VatRegimeSettingsProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const VatRegimeSettings: React.FC<VatRegimeSettingsProps> = ({ onSuccess, onError }) => {
  const { t, i18n } = useTranslation();
  const { refreshVatRegime } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [regime, setRegime] = useState<VatRegime | null>(null);

  const [vatStatus, setVatStatus] = useState<VatStatus | ''>('');
  const [vatNumber, setVatNumber] = useState('');
  const [vatRate, setVatRate] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [legalName, setLegalName] = useState('');
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');

  const chargesVat = vatStatus !== '' && !STATUSES_WITHOUT_VAT.includes(vatStatus);
  const needsVatNumber = vatStatus !== '' && STATUSES_REQUIRING_NUMBER.includes(vatStatus);

  const applyRegime = (data: VatRegime) => {
    setRegime(data);
    setVatStatus(data.vat_status ?? '');
    setVatNumber(data.vat_number ?? '');
    setVatRate(
      data.registration_vat_rate !== null && data.registration_vat_rate !== undefined
        ? String(data.registration_vat_rate)
        : ''
    );
    setCompanyNumber(formatCompanyNumber(data.company_number));
    setLegalName(data.legal_name ?? '');
    setStreet(data.billing_street ?? '');
    setPostalCode(data.billing_postal_code ?? '');
    setCity(data.billing_city ?? '');
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiService.get('/organizations/me/vat');
        applyRegime(res.data as VatRegime);
      } catch (err: any) {
        onError?.(err?.response?.data?.detail || t('settings.vat.loadError'));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!vatStatus) return;
    setSaving(true);
    try {
      const res = await apiService.put('/organizations/me/vat', {
        vat_status: vatStatus,
        vat_number: needsVatNumber ? vatNumber.trim() || null : null,
        company_number: needsVatNumber ? null : companyNumber.trim() || null,
        legal_name: legalName.trim() || null,
        billing_street: street.trim() || null,
        billing_postal_code: postalCode.trim() || null,
        billing_city: city.trim() || null,
        // Un régime sans TVA force le taux à 0, quelle que soit la saisie.
        registration_vat_rate: chargesVat ? Number(vatRate || 0) : 0,
      });
      applyRegime(res.data as VatRegime);
      // Retire la bannière de collecte sans recharger la page.
      await refreshVatRegime();
      onSuccess?.(t('settings.vat.saved'));
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      onError?.(typeof detail === 'string' ? detail : t('settings.vat.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRevalidate = async () => {
    setRevalidating(true);
    try {
      const res = await apiService.post('/organizations/me/vat/revalidate', {});
      applyRegime(res.data as VatRegime);
      onSuccess?.(t('settings.vat.validated'));
    } catch (err: any) {
      const status = err?.response?.status;
      onError?.(
        status === 503
          ? t('settings.vat.viesUnavailable')
          : status === 400
            ? t('settings.vat.viesInvalid')
            : t('settings.vat.validateError')
      );
    } finally {
      setRevalidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isDeclared = Boolean(regime?.is_declared);
  const isValidated = Boolean(regime?.vat_validated_at);
  const isComplete = isDeclared && Boolean(regime?.identity_complete);
  // Numéro confirmé par VIES : le registre fait foi pour le nom légal et le
  // siège, l'organisateur ne les ressaisit pas. Changer de numéro ou de régime
  // rouvre la saisie.
  const viesLocked =
    isValidated &&
    Boolean(regime?.vat_validated_name && regime?.billing_street) &&
    vatStatus === regime?.vat_status &&
    (needsVatNumber
      ? vatNumber.trim() === (regime?.vat_number ?? '')
      : companyNumber.replace(/\D/g, '') === (regime?.company_number ?? ''));
  const orgCountry = regime?.country ?? 'BE';
  const isBelgian = orgCountry === 'BE';
  const isEu = isEuCountry(orgCountry);
  // Numéro européen enregistré mais pas confirmé : VIES ne répondait pas.
  // La tâche de nuit réessaie ; l'écran le dit tant que c'est le cas.
  const awaitingVies =
    isEu && !isValidated && Boolean(regime?.vat_number) && vatNumber.trim() === (regime?.vat_number ?? '');
  // Le numéro BCE n'est exigé qu'en Belgique : ailleurs, chaque registre a ses règles.
  const exemptNeedsNumber = vatStatus === 'exempt_art44' && isBelgian;
  const identityFilled = viesLocked || Boolean(legalName.trim() && street.trim() && postalCode.trim() && city.trim());
  const canSave =
    Boolean(vatStatus) &&
    identityFilled &&
    (!needsVatNumber || Boolean(vatNumber.trim())) &&
    (!exemptNeedsNumber || Boolean(companyNumber.trim()));

  return (
    <div className="space-y-6">
      {!isDeclared && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t('settings.vat.requiredBanner')}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <CardTitle className="text-base md:text-lg">{t('settings.vat.title')}</CardTitle>
            <Badge variant={isComplete ? 'default' : 'secondary'} className="gap-1 w-fit">
              {isComplete ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
              {!isComplete
                ? t('settings.vat.toComplete')
                : isValidated
                  ? t('settings.vat.verifiedVies')
                  : t('settings.vat.complete')}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">{t('settings.vat.intro')}</p>
          <p className="text-sm text-muted-foreground">
            {t('settings.vat.countryLine', {
              country: new Intl.DisplayNames([i18n.language], { type: 'region' }).of(orgCountry),
            })}
          </p>

          <div className="space-y-2">
            <Label>{t('settings.vat.statusLabel')}</Label>
            <Select value={vatStatus} onValueChange={(v) => setVatStatus(v as VatStatus)}>
              <SelectTrigger>
                <SelectValue placeholder={t('settings.vat.statusPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {VAT_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {t(`settings.vat.statuses.${status}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {vatStatus && (
              <p className="text-xs text-muted-foreground">
                {t(`settings.vat.statusHints.${vatStatus}`)}
              </p>
            )}
          </div>

          {vatStatus && needsVatNumber && (
            <div className="space-y-2">
              <Label htmlFor="vat-number">
                {t(isEu ? 'settings.vat.numberLabel' : 'settings.vat.numberLabelOutsideEu')}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  id="vat-number"
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  placeholder={isBelgian ? 'BE0999999999' : isEu ? `${vatPrefix(orgCountry)}…` : undefined}
                  className="font-mono"
                />
                {regime?.vat_number && isEu && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRevalidate}
                    disabled={revalidating}
                    className="gap-2 shrink-0"
                  >
                    {revalidating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {t('settings.vat.revalidate')}
                  </Button>
                )}
              </div>
              {!isEu ? (
                <p className="text-xs text-muted-foreground">{t('settings.vat.numberHintOutsideEu')}</p>
              ) : awaitingVies ? (
                <p className="text-xs text-warning">{t('settings.vat.numberPendingVies')}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{t('settings.vat.numberHint')}</p>
              )}
            </div>
          )}

          {vatStatus && !needsVatNumber && (
            <div className="space-y-2">
              <Label htmlFor="company-number">
                {t(isBelgian ? 'settings.vat.companyNumberLabel' : 'settings.vat.companyNumberLabelOther')}
                {exemptNeedsNumber && <span className="text-destructive ml-1">*</span>}
              </Label>
              <Input
                id="company-number"
                value={companyNumber}
                onChange={(e) => setCompanyNumber(e.target.value)}
                placeholder={isBelgian ? '0999.999.999' : undefined}
                className="font-mono max-w-[240px]"
              />
              <p className="text-xs text-muted-foreground">
                {t(isBelgian ? 'settings.vat.companyNumberHint' : 'settings.vat.companyNumberHintOther')}
              </p>
            </div>
          )}

          {vatStatus && (
            <div className="space-y-4 border-t pt-5">
              <div>
                <p className="text-sm font-medium">{t('settings.vat.identityTitle')}</p>
                <p className="text-xs text-muted-foreground">
                  {viesLocked ? t('settings.vat.fromVies') : t('settings.vat.identityHint')}
                </p>
              </div>

              {viesLocked ? (
                <div className="rounded-md border bg-muted/40 p-3 space-y-1">
                  <p className="text-sm">{regime?.legal_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {[regime?.billing_street, [regime?.billing_postal_code, regime?.billing_city].filter(Boolean).join(' ')]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="legal-name">
                      {t('settings.vat.legalNameLabel')}
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      id="legal-name"
                      value={legalName}
                      onChange={(e) => setLegalName(e.target.value)}
                      placeholder={t('settings.vat.legalNamePlaceholder')}
                    />
                    <p className="text-xs text-muted-foreground">{t('settings.vat.legalNameHint')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billing-street">
                      {t('settings.vat.streetLabel')}
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input id="billing-street" value={street} onChange={(e) => setStreet(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-[120px_1fr] gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="billing-postal">
                        {t('settings.vat.postalCodeLabel')}
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input
                        id="billing-postal"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billing-city">
                        {t('settings.vat.cityLabel')}
                        <span className="text-destructive ml-1">*</span>
                      </Label>
                      <Input id="billing-city" value={city} onChange={(e) => setCity(e.target.value)} />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {chargesVat && (
            <div className="space-y-2">
              <Label htmlFor="vat-rate">{t('settings.vat.rateLabel')}</Label>
              <NumberInput
                id="vat-rate"
                min={0}
                max={100}
                step="0.01"
                value={vatRate}
                onChange={(e) => setVatRate(e.target.value)}
                placeholder={isBelgian ? '21' : undefined}
                className="max-w-[160px]"
              />
              <p className="text-xs text-muted-foreground">{t('settings.vat.rateHint')}</p>
            </div>
          )}

          <p className="text-xs text-muted-foreground">{t('settings.vat.responsibility')}</p>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || !canSave}
              className="gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VatRegimeSettings;
